import React, { useEffect, useRef, useState } from 'react';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

// --- Pure handlers extracted so the component body stays small. ---

const hydrateText = (el, value) => {
  if (el && document.activeElement !== el) {
    el.innerText = value || '';
  }
};

const persistEdit = async ({ ref, value, k, saveContent, setSaving }) => {
  const newVal = ref.current?.innerText ?? '';
  if (newVal === value) return;
  setSaving(true);
  try {
    await saveContent(k, newVal);
  } catch {
    ref.current.innerText = value || '';
  } finally {
    setSaving(false);
  }
};

const onEditorKeyDown = (e, ref, value, multiline) => {
  if (!multiline && e.key === 'Enter') {
    e.preventDefault();
    ref.current.blur();
    return;
  }
  if (e.key === 'Escape') {
    ref.current.innerText = value || '';
    ref.current.blur();
  }
};

/**
 * <EditableText k="hero.title">fallback text</EditableText>
 * - When edit mode is on: contentEditable with dashed outline
 * - When off: renders text only
 */
const EditableText = ({ k, fallback = '', as: Tag = 'span', className = '', multiline = false, ...rest }) => {
  const { t, saveContent, editMode } = useCMS();
  const { isAuthed } = useAuth();
  const ref = useRef(null);
  const [saving, setSaving] = useState(false);
  const value = t(k, fallback);

  useEffect(() => { hydrateText(ref.current, value); }, [value]);

  if (!isAuthed || !editMode) {
    return <Tag className={className} {...rest}>{value}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={() => persistEdit({ ref, value, k, saveContent, setSaving })}
      onKeyDown={(e) => onEditorKeyDown(e, ref, value, multiline)}
      data-cms-key={k}
      className={`${className} cms-editable ${saving ? 'cms-saving' : ''}`}
      {...rest}
    >
      {value}
      {saving && <Loader2 size={12} className="inline animate-spin ml-1" />}
    </Tag>
  );
};

export default EditableText;
